import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Briefcase, FileText, Calendar, CreditCard, AlertCircle, CheckCircle, 
  Edit, Loader2, MapPin, Mail, Phone, MessageSquare, Plus, Trash2,
  User, Car, Flag, FileCheck, Inbox, Bell, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = { pending: "In attesa", reviewed: "Visionato", accepted: "Accettato", rejected: "Rifiutato" };
const statusColors = { pending: "bg-amber-100 text-amber-700", reviewed: "bg-blue-100 text-blue-700", accepted: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };

const PATENTI_OPTIONS = [
  { value: "B", label: "B (Auto)" },
  { value: "C", label: "C (Camion)" },
  { value: "C1", label: "C1 (Camion leggero)" },
  { value: "D", label: "D (Autobus)" },
  { value: "A", label: "A (Moto)" },
];

const PERMESSO_OPTIONS = [
  { value: "nessuno", label: "Nessuno" },
  { value: "L", label: "L (Permesso di dimora di breve durata)" },
  { value: "B", label: "B (Permesso di dimora)" },
  { value: "C", label: "C (Permesso di domicilio)" },
  { value: "G", label: "G (Frontaliere)" },
];

const DISPONIBILITA_OPTIONS = [
  { value: "subito", label: "Subito" },
  { value: "2giorni", label: "2 giorni" },
  { value: "1settimana", label: "Una settimana" },
  { value: "2settimane", label: "Due settimane" },
  { value: "1mese", label: "Un mese" },
  { value: "2mesi", label: "Due mesi" },
  { value: "3mesi", label: "3 mesi" },
];

export default function CandidateDashboard() {
  const { user, refreshUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: "",
    comune_residenza: "",
    bio: "",
    esperienze: [],
    contact_method: "email",
    phone: "",
    // Nuovi campi anagrafici CH
    eta: "",
    patenti: [],
    cittadinanza: "",
    permesso_svizzero: "nessuno",
    disponibilita: "subito"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = await refreshUser();
        if (userData) {
          // Migrazione: se esperienze è una stringa, convertila in array
          let esperienze = userData.esperienze || [];
          if (typeof esperienze === "string" && esperienze.trim()) {
            esperienze = [{ posizione: "Esperienza precedente", anni: "", descrizione: esperienze }];
          }
          
          setProfileForm({
            name: userData.name || "",
            comune_residenza: userData.comune_residenza || "",
            bio: userData.bio || "",
            esperienze: Array.isArray(esperienze) ? esperienze : [],
            contact_method: userData.contact_method || "email",
            phone: userData.phone || "",
            eta: userData.eta || "",
            patenti: userData.patenti || [],
            cittadinanza: userData.cittadinanza || "",
            permesso_svizzero: userData.permesso_svizzero || "nessuno",
            disponibilita: userData.disponibilita || "subito"
          });
        }
        if (user?.subscription_active) {
          const appsRes = await axios.get(`${API}/applications`);
          setApplications(appsRes.data);
        }
        
        // Carica messaggi ricevuti
        try {
          const [inboxRes, unreadRes] = await Promise.all([
            axios.get(`${API}/messages/inbox`),
            axios.get(`${API}/messages/unread-count`)
          ]);
          setMessages(inboxRes.data.slice(0, 5)); // Ultimi 5 messaggi
          setUnreadCount(unreadRes.data.count || 0);
        } catch (msgError) {
          console.error("Failed to fetch messages:", msgError);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gestione esperienze
  const addEsperienza = () => {
    setProfileForm({
      ...profileForm,
      esperienze: [...profileForm.esperienze, { posizione: "", anni: "", descrizione: "" }]
    });
  };

  const updateEsperienza = (index, field, value) => {
    const newEsperienze = [...profileForm.esperienze];
    newEsperienze[index] = { ...newEsperienze[index], [field]: value };
    setProfileForm({ ...profileForm, esperienze: newEsperienze });
  };

  const removeEsperienza = (index) => {
    const newEsperienze = profileForm.esperienze.filter((_, i) => i !== index);
    setProfileForm({ ...profileForm, esperienze: newEsperienze });
  };

  // Gestione patenti
  const togglePatente = (patente) => {
    const newPatenti = profileForm.patenti.includes(patente)
      ? profileForm.patenti.filter(p => p !== patente)
      : [...profileForm.patenti, patente];
    setProfileForm({ ...profileForm, patenti: newPatenti });
  };

  const handleSaveProfile = async () => {
    if (!profileForm.comune_residenza) {
      toast.error("Il comune di residenza è obbligatorio");
      return;
    }
    if (profileForm.contact_method === "phone" && !profileForm.phone) {
      toast.error("Inserisci il numero di telefono");
      return;
    }
    if (!profileForm.disponibilita) {
      toast.error("Seleziona la tua disponibilità");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        name: profileForm.name,
        comune_residenza: profileForm.comune_residenza,
        bio: profileForm.bio || null,
        esperienze: profileForm.esperienze,
        contact_method: profileForm.contact_method,
        phone: profileForm.phone || null,
        eta: profileForm.eta ? parseInt(profileForm.eta) : null,
        patenti: profileForm.patenti,
        cittadinanza: profileForm.cittadinanza || null,
        permesso_svizzero: profileForm.permesso_svizzero,
        disponibilita: profileForm.disponibilita
      });
      await refreshUser();
      toast.success("Profilo aggiornato!");
      setShowEditProfile(false);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("it-CH", { day: "numeric", month: "short", year: "numeric" });

  const getContactIcon = (method) => {
    switch (method) {
      case "email": return <Mail className="w-4 h-4" />;
      case "phone": return <Phone className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getContactLabel = (method) => {
    switch (method) {
      case "email": return "Email";
      case "phone": return "Telefono/WhatsApp";
      default: return "Messaggistica interna";
    }
  };

  const getPermessoLabel = (value) => {
    const option = PERMESSO_OPTIONS.find(o => o.value === value);
    return option ? option.label : value;
  };

  const getDisponibilitaLabel = (value) => {
    const option = DISPONIBILITA_OPTIONS.find(o => o.value === value);
    return option ? option.label : value;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="candidate-dashboard">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Ciao, {user?.name}!</h1>
            <p className="text-slate-600 mt-1">Gestisci il tuo profilo e le candidature</p>
          </div>
          <Button variant="outline" onClick={() => setShowEditProfile(true)} data-testid="edit-profile-btn">
            <Edit className="w-4 h-4 mr-2" /> Modifica profilo
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Il tuo profilo</CardTitle>
                <CardDescription>Questi dati vengono inviati quando ti candidi</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div><span className="text-slate-500">Nome:</span> <span className="font-medium">{user?.name}</span></div>
                
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-500">Comune:</span> 
                  <span className="font-medium">{user?.comune_residenza || <span className="text-red-500">Non specificato</span>}</span>
                </div>

                {/* Nuovi dati anagrafici */}
                {user?.eta && (
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Età:</span>
                    <span className="font-medium">{user.eta} anni</span>
                  </div>
                )}

                {user?.cittadinanza && (
                  <div className="flex items-center gap-1">
                    <Flag className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Cittadinanza:</span>
                    <span className="font-medium">{user.cittadinanza}</span>
                  </div>
                )}

                {user?.permesso_svizzero && user.permesso_svizzero !== "nessuno" && (
                  <div className="flex items-center gap-1">
                    <FileCheck className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Permesso:</span>
                    <span className="font-medium">{getPermessoLabel(user.permesso_svizzero)}</span>
                  </div>
                )}

                {user?.patenti && user.patenti.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Car className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Patenti:</span>
                    <span className="font-medium">{user.patenti.join(", ")}</span>
                  </div>
                )}

                {user?.disponibilita && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Disponibilità:</span>
                    <span className="font-medium">{getDisponibilitaLabel(user.disponibilita)}</span>
                  </div>
                )}
                
                {user?.bio && (
                  <div>
                    <span className="text-slate-500">Bio:</span>
                    <p className="mt-1 p-2 bg-slate-50 rounded text-slate-700">{user.bio}</p>
                  </div>
                )}

                {/* Esperienze strutturate */}
                {user?.esperienze && Array.isArray(user.esperienze) && user.esperienze.length > 0 && (
                  <div>
                    <span className="text-slate-500">Esperienze:</span>
                    <div className="mt-2 space-y-2">
                      {user.esperienze.map((exp, idx) => (
                        <div key={`exp-${idx}-${exp.posizione}`} className="p-2 bg-slate-50 rounded">
                          <div className="font-medium text-slate-800">{exp.posizione}</div>
                          {exp.anni && <div className="text-xs text-slate-500">{exp.anni} anni</div>}
                          {exp.descrizione && <p className="text-slate-600 mt-1 text-xs">{exp.descrizione}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Fallback per esperienze legacy (stringa) */}
                {user?.esperienze && typeof user.esperienze === "string" && (
                  <div>
                    <span className="text-slate-500">Esperienze:</span>
                    <p className="mt-1 p-2 bg-slate-50 rounded text-slate-700 whitespace-pre-wrap">{user.esperienze}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2 border-t">
                  {getContactIcon(user?.contact_method)}
                  <span className="text-slate-500">Contatto:</span>
                  <span className="font-medium">{getContactLabel(user?.contact_method)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Account Status Card - FREE for candidates */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" /> Account Attivo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-2 text-emerald-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Gratuito</span>
                </div>
                <p className="text-sm text-slate-600">
                  Il tuo account candidato è attivo e gratuito. Puoi candidarti a tutti gli annunci senza limiti.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <Link to="/jobs">
                  <Button variant="outline" className="w-full justify-start">
                    <Briefcase className="w-4 h-4 mr-2" /> Cerca annunci
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Messages Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Inbox className="w-5 h-5 text-[#004E64]" /> Messaggi
                  </div>
                  {unreadCount > 0 && (
                    <Badge className="bg-[#DA291C] text-white">
                      {unreadCount} {unreadCount === 1 ? "nuovo" : "nuovi"}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {messages.length > 0 ? (
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`p-3 rounded-lg border ${!msg.is_read ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className={`text-sm truncate ${!msg.is_read ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                              {msg.sender_name}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{msg.subject}</p>
                          </div>
                          {!msg.is_read && (
                            <Bell className="w-4 h-4 text-[#DA291C] flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    ))}
                    <Link to="/messages">
                      <Button variant="outline" className="w-full mt-2">
                        <MessageSquare className="w-4 h-4 mr-2" /> Vedi tutti i messaggi
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Inbox className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Nessun messaggio</p>
                    <p className="text-xs text-slate-400 mt-1">Le aziende ti contatteranno qui</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Applications */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#004E64]" /> Le tue candidature
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8"><div className="spinner"></div></div>
                ) : applications.length > 0 ? (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div key={app.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-lg gap-4">
                        <div className="flex-1 min-w-0">
                          <Link to={`/jobs/${app.job_id}`} className="font-semibold text-slate-900 hover:text-[#004E64]">{app.job_title}</Link>
                          <p className="text-sm text-slate-500 mt-1">{app.company_name} • {formatDate(app.created_at)}</p>
                        </div>
                        <Badge className={statusColors[app.status]}>{statusLabels[app.status]}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessuna candidatura</h3>
                    <Link to="/jobs"><Button variant="outline">Cerca annunci</Button></Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica profilo</DialogTitle>
            <DialogDescription>Questi dati vengono inviati alle aziende quando ti candidi</DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            {/* Dati base */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input 
                  value={profileForm.name} 
                  onChange={(e) => setProfileForm({...profileForm, name: e.target.value})} 
                  className="mt-1.5" 
                  data-testid="profile-name"
                />
              </div>
              <div>
                <Label>Comune di residenza *</Label>
                <Input 
                  value={profileForm.comune_residenza} 
                  onChange={(e) => setProfileForm({...profileForm, comune_residenza: e.target.value})} 
                  placeholder="Lugano" 
                  className="mt-1.5"
                  data-testid="profile-comune"
                />
              </div>
            </div>

            {/* Dati anagrafici CH */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Flag className="w-4 h-4" /> Dati anagrafici e permessi
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Età</Label>
                  <Input 
                    type="number" 
                    min="16" 
                    max="99"
                    value={profileForm.eta} 
                    onChange={(e) => setProfileForm({...profileForm, eta: e.target.value})} 
                    placeholder="Es. 35"
                    className="mt-1.5"
                    data-testid="profile-eta"
                  />
                </div>
                <div>
                  <Label>Cittadinanza</Label>
                  <Input 
                    value={profileForm.cittadinanza} 
                    onChange={(e) => setProfileForm({...profileForm, cittadinanza: e.target.value})} 
                    placeholder="Es. Italiana, Svizzera"
                    className="mt-1.5"
                    data-testid="profile-cittadinanza"
                  />
                </div>
                <div>
                  <Label>Permesso svizzero</Label>
                  <Select 
                    value={profileForm.permesso_svizzero} 
                    onValueChange={(v) => setProfileForm({...profileForm, permesso_svizzero: v})}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="profile-permesso">
                      <SelectValue placeholder="Seleziona permesso" />
                    </SelectTrigger>
                    <SelectContent>
                      {PERMESSO_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Patenti</Label>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {PATENTI_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center gap-2">
                        <Checkbox 
                          id={`patente-${opt.value}`}
                          checked={profileForm.patenti.includes(opt.value)}
                          onCheckedChange={() => togglePatente(opt.value)}
                          data-testid={`patente-${opt.value}`}
                        />
                        <Label htmlFor={`patente-${opt.value}`} className="text-sm font-normal cursor-pointer">
                          {opt.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Disponibilità */}
                <div>
                  <Label>Disponibile da: <span className="text-red-500">*</span></Label>
                  <Select 
                    value={profileForm.disponibilita} 
                    onValueChange={(v) => setProfileForm({...profileForm, disponibilita: v})}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="profile-disponibilita">
                      <SelectValue placeholder="Seleziona disponibilità..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DISPONIBILITA_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <Label>Breve bio (opzionale)</Label>
              <Textarea 
                value={profileForm.bio} 
                onChange={(e) => setProfileForm({...profileForm, bio: e.target.value})} 
                placeholder="Presentati brevemente..." 
                rows={2} 
                className="mt-1.5"
                data-testid="profile-bio"
              />
            </div>

            {/* Esperienze strutturate */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Esperienze lavorative
                </h3>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={addEsperienza}
                  data-testid="add-esperienza-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
              
              {profileForm.esperienze.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded">
                  Nessuna esperienza aggiunta. Clicca "Aggiungi" per iniziare.
                </p>
              ) : (
                <div className="space-y-4">
                  {profileForm.esperienze.map((exp, index) => (
                    <div key={`edit-exp-${index}-${exp.posizione || 'new'}`} className="p-4 border rounded-lg bg-slate-50 relative">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeEsperienza(index)}
                        data-testid={`remove-esperienza-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pr-8">
                        <div>
                          <Label className="text-xs">Posizione/Titolo *</Label>
                          <Input
                            value={exp.posizione}
                            onChange={(e) => updateEsperienza(index, "posizione", e.target.value)}
                            placeholder="Es. Muratore, Elettricista"
                            className="mt-1"
                            data-testid={`esperienza-posizione-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Anni di esperienza</Label>
                          <Input
                            type="number"
                            min="0"
                            max="50"
                            value={exp.anni}
                            onChange={(e) => updateEsperienza(index, "anni", e.target.value)}
                            placeholder="Es. 5"
                            className="mt-1"
                            data-testid={`esperienza-anni-${index}`}
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs">Descrizione attività</Label>
                        <Textarea
                          value={exp.descrizione}
                          onChange={(e) => updateEsperienza(index, "descrizione", e.target.value)}
                          placeholder="Descrivi brevemente le attività svolte..."
                          rows={2}
                          className="mt-1"
                          data-testid={`esperienza-descrizione-${index}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Contatto */}
            <div className="border-t pt-4">
              <Label>Come vuoi essere contattato?</Label>
              <RadioGroup 
                value={profileForm.contact_method} 
                onValueChange={(v) => setProfileForm({...profileForm, contact_method: v})} 
                className="mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="email" id="edit-email" />
                  <Label htmlFor="edit-email" className="font-normal">Email</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="phone" id="edit-phone" />
                  <Label htmlFor="edit-phone" className="font-normal">Telefono/WhatsApp</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="internal" id="edit-internal" />
                  <Label htmlFor="edit-internal" className="font-normal">Messaggistica interna</Label>
                </div>
              </RadioGroup>
            </div>

            {profileForm.contact_method === "phone" && (
              <div>
                <Label>Numero di telefono *</Label>
                <Input 
                  value={profileForm.phone} 
                  onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} 
                  placeholder="+41 79 123 45 67" 
                  className="mt-1.5"
                  data-testid="profile-phone"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProfile(false)}>Annulla</Button>
            <Button 
              onClick={handleSaveProfile} 
              disabled={saving} 
              className="bg-[#DA291C] hover:bg-[#B91C1C] text-white"
              data-testid="save-profile-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</> : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
